#!/usr/bin/env node
import { Command } from "commander";
import { PublicKey } from "@solana/web3.js";
import packageJson from "../package.json";
import { addConfigCommands } from "@crunchdao/shared-cli";

// Import commands
import { DepositCommand } from "./commands/deposit";
import { WithdrawCommand } from "./commands/withdraw";
import { DelegateCommand } from "./commands/delegate";
import { UndelegateCommand } from "./commands/undelegate";
import { GetPositionsCommand } from "./commands/get-positions";
import { GetAvailableCommand } from "./commands/get-available";
import { GetRewardsCommand } from "./commands/get-rewards";
import { ClaimCommand } from "./commands/claim";
import { ListAccountsCommand } from "./commands/list-accounts";
import { ListPositionsAccountsCommand } from "./commands/list-positions-accounts";

const program = new Command();

program
  .name("crunch-staker")
  .description("CrunchDAO Staker CLI")
  .version(packageJson.version)
  .option("-u, --url <URL_OR_MONIKER>", "URL for Solana's JSON RPC or moniker")
  .option("-w, --wallet <wallet>", "Path to wallet keypair")
  .option("-l, --loglevel <loglevel>", "Log level for program output")
  .option("-o, --output <format>", "Output format (json)");

// Deposit command
program
  .command("deposit")
  .description("Deposit CRNCH tokens into staking account")
  .argument("<amount>", "Amount of CRNCH to deposit")
  .action(async (amount) => {
    const opts = program.opts();
    const cmd = new DepositCommand(opts);
    await cmd.execute(parseFloat(amount));
  });

// Withdraw command
program
  .command("withdraw")
  .description("Withdraw CRNCH tokens from staking account")
  .argument("<amount>", "Amount of CRNCH to withdraw")
  .action(async (amount) => {
    const opts = program.opts();
    const cmd = new WithdrawCommand(opts);
    await cmd.execute(parseFloat(amount));
  });

// Delegate command
program
  .command("delegate")
  .description("Delegate tokens to a coordinator")
  .argument("<coordinator>", "Coordinator public key")
  .argument("<amount>", "Amount of CRNCH to delegate")
  .action(async (coordinator, amount) => {
    const opts = program.opts();
    const cmd = new DelegateCommand(opts);
    await cmd.execute(new PublicKey(coordinator), parseFloat(amount));
  });

// Undelegate command
program
  .command("undelegate")
  .description("Undelegate tokens from a coordinator")
  .argument("<coordinator>", "Coordinator public key")
  .argument("<amount>", "Amount of CRNCH to undelegate")
  .action(async (coordinator, amount) => {
    const opts = program.opts();
    const cmd = new UndelegateCommand(opts);
    await cmd.execute(new PublicKey(coordinator), parseFloat(amount));
  });

// Get positions command
program
  .command("positions")
  .description("Get all staking positions for your account")
  .action(async () => {
    const opts = program.opts();
    const cmd = new GetPositionsCommand(opts);
    await cmd.execute();
  });

// Get available amount command
program
  .command("available")
  .description("Get available amount to stake")
  .action(async () => {
    const opts = program.opts();
    const cmd = new GetAvailableCommand(opts);
    await cmd.execute();
  });

// Get rewards command
program
  .command("rewards")
  .description("Get claimable rewards")
  .action(async () => {
    const opts = program.opts();
    const cmd = new GetRewardsCommand(opts);
    await cmd.execute();
  });

// Claim command
program
  .command("claim")
  .description("Claim rewards from all coordinators")
  .action(async () => {
    const opts = program.opts();
    const cmd = new ClaimCommand(opts);
    await cmd.execute();
  });

// List all accounts command
program
  .command("list-accounts")
  .description("List all staking accounts")
  .action(async () => {
    const opts = program.opts();
    const cmd = new ListAccountsCommand(opts);
    await cmd.execute();
  });

// List all positions accounts command
program
  .command("list-positions-accounts")
  .description("List all StakingPositions accounts")
  .action(async () => {
    const opts = program.opts();
    const cmd = new ListPositionsAccountsCommand(opts);
    await cmd.execute();
  });

// Add config commands
addConfigCommands(program);

program.parse(process.argv);

if (!process.argv.slice(2).length) {
  program.outputHelp();
}
